import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, Droplets } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navLinks = [
  { to: "/", label: "Acasă", end: true },
  { to: "/magazin", label: "Magazin" },
  { to: "/magazin?category=Detergenți", label: "Detergenți" },
  { to: "/magazin?category=Balsam de rufe", label: "Balsam de rufe" },
  { to: "/magazin?category=Pachete promo", label: "Pachete promo" },
  { to: "/recenzii", label: "Recenzii" },
  { to: "/contact", label: "Despre noi" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();

  const linkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 hover:text-primary ${
      isActive ? "text-primary" : "text-slate-600"
    }`;

  return (
    <header
      className="sticky top-0 z-50 border-b border-white/40 bg-white/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Droplets className="h-5 w-5" />
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
            Clar<span className="text-primary">Wash</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {navLinks.map((l) => (
            <NavLink key={l.label} to={l.to} end={l.end} className={linkClass} data-testid={`nav-${l.label}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/cos" className="relative" data-testid="cart-button">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ShoppingCart className="h-5 w-5" />
            </Button>
            {count > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-xs font-bold text-white"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="account-menu-trigger">
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Panou Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/cont")} data-testid="menu-account">
                  <User className="mr-2 h-4 w-4" /> Contul meu
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Deconectare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/autentificare")}
              className="hidden rounded-full sm:inline-flex"
              data-testid="login-button"
            >
              Autentificare
            </Button>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full lg:hidden" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="mt-8 flex flex-col gap-4">
                {navLinks.map((l) => (
                  <NavLink key={l.label} to={l.to} end={l.end} className="text-base font-medium text-slate-700">
                    {l.label}
                  </NavLink>
                ))}
                {!user && (
                  <Button onClick={() => navigate("/autentificare")} className="mt-2 rounded-full">
                    Autentificare
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
